import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

const Control = styled.textarea`
  resize: none;
  position: relative;
  padding: 1rem;
  width: 100%;
  outline: 0;
  background-color: transparent;
  font-size: 1rem;
  font-weight: var(--font-weight);

  border-radius: var(--border-radius-form);
  border: var(--border-form);
  transition-property: border-color, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  ${props =>
    props.hasError &&
    css`
      border-color: var(--color-error);
    `}

  &:disabled {
    opacity: 0.5;
  }

  &:focus {
    border-color: var(--color-primary);
  }

  &::placeholder {
    opacity: 0;
  }

  &:required {
    box-shadow: none !important;
  }
`

const Placeholder = styled.span`
  display: flex;
  align-items: center;
  user-select: none;
  border-radius: var(--border-radius-form);

  color: var(--color-fg-light);
  position: absolute;

  transition-property: font-size, height, background-color, padding, top, color;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  background-color: var(--color-bg-form);
  height: 1rem;
  padding: 0 0.25rem;
  font-size: 0.75em;
  top: 0;
  left: 0.75rem;
  pointer-events: none;
  overflow-x: visible;
  white-space: nowrap;

  &:empty {
    display: none;
  }

  ${Control}:placeholder-shown ~ & {
    background-color: transparent;
    top: 0.5rem;
    height: 2.5rem;
    padding: 0 0.25rem;
    font-size: 1em;
  }

  ${props =>
    props.hasError &&
    css`
      color: var(--color-error);
      ${Control}:placeholder-shown ~ & {
        color: var(--color-fg-light);
      }
    `}

  ${Control}:focus ~ & {
    background-color: var(--color-bg-form);
    color: var(--color-primary);
    height: 1rem;
    padding: 0 0.25rem;
    font-size: 0.75em;
    top: 0;
    left: 0.75rem;
  }
`

const ControlBase = styled.label`
  position: relative;
  display: block;
  width: 100%;
  padding-top: 0.5rem;
  font-size: 0.875rem;
`

const ComponentBase = styled.div`
  margin-bottom: 0.625rem;
`

const ErrorMessage = styled.output`
  display: block;
  color: var(--color-error);
  padding: 0 1rem;
  font-size: 0.75em;
  margin-top: 0.25rem;
  overflow: hidden;
  height: 1.125rem;
  transition-property: height, opacity;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);

  :empty {
    height: 0;
    opacity: 0;
  }

  ${ControlBase}:focus-within ~ & {
    height: 0;
    opacity: 0;
  }
`

class TextArea extends React.Component {
  input = React.createRef()

  componentDidMount() {
    const { initialValue } = this.props

    if (this.input.current) {
      this.input.current.value = initialValue
    }
  }

  componentDidUpdate(prevProps) {
    const { initialValue } = this.props

    if (prevProps.initialValue !== initialValue) {
      this.input.current.value = initialValue
    }
  }

  render() {
    const { placeholder, error, touched, ...props } = this.props

    return (
      <ComponentBase>
        <ControlBase>
          <Control
            {...props}
            placeholder={placeholder}
            ref={this.input}
            hasError={touched && error}
          />
          <Placeholder hasError={touched && error}>{placeholder}</Placeholder>
        </ControlBase>
        <ErrorMessage>{touched && error}</ErrorMessage>
      </ComponentBase>
    )
  }
}

TextArea.defaultProps = {
  placeholder: '',
  initialValue: '',
  error: null,
  touched: false,
}

TextArea.propTypes = {
  placeholder: PropTypes.string,
  initialValue: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
}

export default TextArea
